import React, { useState, useEffect } from 'react'
import AgentLayout from '../../components/agent/AgentLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'

export default function AgentProfile() {
    const { user, token, updateUser } = useAuth()
    const [saving, setSaving] = useState(false)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [form, setForm] = useState({
        name: '', phone: '', title: '', address: '', city: '', state: '', zip: '', about: '',
        facebook: '', twitter: '', googleplus: '', linkedin: ''
    })

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || '', phone: user.phone || '', title: user.title || '',
                address: user.address || '', city: user.city || '', state: user.state || '',
                zip: user.zip || '', about: user.about || '',
                facebook: user.facebook || '', twitter: user.twitter || '',
                googleplus: user.googleplus || '', linkedin: user.linkedin || ''
            })
        }
    }, [user])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) return
        setSaving(true); setMsg(null)
        try {
            const data = await api.updateMe(token, form)
            if (data && data._id) {
                updateUser(data)
                setMsg({ type: 'success', text: 'Profile updated successfully!' })
            } else {
                setMsg({ type: 'error', text: data?.message || 'Failed to update profile.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Server error. Please try again.' })
        } finally {
            setSaving(false)
        }
    }

    return (
        <AgentLayout>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="fw-bold mb-1">My Profile</h3>
                    <p className="text-muted mb-0">Manage your personal information and social accounts.</p>
                </div>
            </div>

            {msg && (
                <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'} border-0 shadow-sm mb-4`}>
                    <i className={`fa-solid ${msg.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'} me-2`}></i>
                    {msg.text}
                </div>
            )}

            <form onSubmit={handleSave}>
                <div className="row g-4">
                    {/* Basic Info */}
                    <div className="col-lg-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Basic Information</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted text-uppercase">Full Name</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="name" value={form.name} onChange={handleChange} required />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted text-uppercase">Email Address (Read-only)</label>
                                        <input type="email" className="form-control bg-light border-0 py-2" value={user?.email || ''} disabled readOnly />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted text-uppercase">Professional Title</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="title" value={form.title} onChange={handleChange} placeholder="e.g. Senior Real Estate Agent" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted text-uppercase">Phone Number</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="phone" value={form.phone} onChange={handleChange} placeholder="Your contact number" />
                                    </div>
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold small text-muted text-uppercase">About / Bio</label>
                                        <textarea className="form-control bg-light border-0 py-2" name="about" value={form.about} onChange={handleChange} rows={4} placeholder="Write a short bio..."></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Location Info */}
                    <div className="col-lg-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Office / Location</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label className="form-label fw-bold small text-muted text-uppercase">Address</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="address" value={form.address} onChange={handleChange} placeholder="Street address" />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold small text-muted text-uppercase">City</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="city" value={form.city} onChange={handleChange} placeholder="City" />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold small text-muted text-uppercase">State</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="state" value={form.state} onChange={handleChange} placeholder="State" />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label fw-bold small text-muted text-uppercase">Zip Code</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="zip" value={form.zip} onChange={handleChange} placeholder="Zip code" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Social Accounts */}
                    <div className="col-lg-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-header bg-white py-3 border-bottom">
                                <h5 className="mb-0 fw-bold">Social Media Profiles</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted text-uppercase"><i className="fa-brands fa-facebook text-primary me-2"></i>Facebook</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="facebook" value={form.facebook} onChange={handleChange} placeholder="Link to Facebook" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted text-uppercase"><i className="fa-brands fa-twitter text-info me-2"></i>Twitter</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="twitter" value={form.twitter} onChange={handleChange} placeholder="Link to Twitter" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted text-uppercase"><i className="fa-brands fa-linkedin text-primary me-2"></i>LinkedIn</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="linkedin" value={form.linkedin} onChange={handleChange} placeholder="Link to LinkedIn" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-bold small text-muted text-uppercase"><i className="fa-brands fa-google text-danger me-2"></i>Google Plus</label>
                                        <input type="text" className="form-control bg-light border-0 py-2" name="googleplus" value={form.googleplus} onChange={handleChange} placeholder="Link to Google+" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-12 mb-5">
                        <div className="d-grid">
                            <button className="btn btn-success py-3 fw-bold rounded-4 shadow-sm border-0 transition-all" type="submit" disabled={saving}>
                                {saving ? (
                                    <><span className="spinner-border spinner-border-sm me-2"></span> Updating Profile...</>
                                ) : 'Update My Profile'}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </AgentLayout>
    )
}
